"use client";

import { motion } from "framer-motion";
import { GlowingButton } from "@/components/ui/GlowingButton";
import { TechCard } from "@/components/ui/TechCard";
import { Brain, Cpu, Network, ShieldCheck, Zap, Terminal, Send } from "lucide-react";
import Link from "next/link";
import { PricingSection } from "@/components/sections/PricingSection";

export default function Home() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-6 drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
              超越极限的 <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-cyan to-neon-blue drop-shadow-[0_0_10px_rgba(0,243,255,0.5)]">扑克人工智能</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/95 mb-10 max-w-3xl mx-auto font-medium leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
              基于数十亿级对局数据训练的深度神经网络。为您提供顶尖的 GTO 策略、完美的防封号机制以及强大的 API 接口。
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-6">
              <a href="https://kingcrasx.cn/wwzs2.apk" target="_blank" rel="noopener noreferrer" className="block w-full sm:w-64">
                <GlowingButton variant="blue" className="w-full h-full flex items-center justify-center gap-2">
                  <span className="whitespace-nowrap">下载应用</span>
                </GlowingButton>
              </a>
              <Link href="/api-services" className="block w-full sm:w-64">
                <GlowingButton variant="cyan" className="w-full h-full flex items-center justify-center">
                  <span className="whitespace-nowrap">接入 API 服务</span>
                </GlowingButton>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative border-t border-white/10 bg-black/40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold tracking-wide mb-4">重新定义扑克博弈</h2>
            <div className="w-24 h-1 bg-neon-cyan mx-auto rounded-full shadow-[0_0_10px_rgba(0,243,255,0.5)]"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <TechCard 
              title="百亿级数据训练" 
              description="我们的神经网络模型在超过70亿手真实的扑克对局中进行了深度强化学习，形成了无法被剥削的绝对 GTO 策略底座。" 
              icon={<Database className="w-8 h-8" />} 
              delay={0.1}
            />
            <TechCard 
              title="实时 AI 剥削机制" 
              description="不止于 GTO。系统能够实时分析对手的历史数据（VPIP, PFR, 3-Bet），动态调整打法，最大化对局期望值（EV）。" 
              icon={<Brain className="w-8 h-8" />} 
              delay={0.2}
            />
            <TechCard 
              title="军工级防封号伪装" 
              description="模拟人类真实的点击延迟、视线移动和疲劳特征。配合底层 IP 和 GPS 伪装，完美绕过各大平台的反作弊检测。" 
              icon={<ShieldCheck className="w-8 h-8" />} 
              delay={0.3}
            />
            <TechCard 
              title="全平台协议支持" 
              description="支持超过 20+ 主流扑克平台，包括 NLH, PLO4/5/6, 短牌等各种玩法，一键无缝接入您的俱乐部。" 
              icon={<Network className="w-8 h-8" />} 
              delay={0.4}
            />
            <TechCard 
              title="毫秒级决策响应" 
              description="全球分布式 GPU 集群提供算力支撑，在复杂的多人底池中也能在 200 毫秒内给出完美解答。" 
              icon={<Zap className="w-8 h-8" />} 
              delay={0.5}
            />
            <TechCard 
              title="开发者友好的 API" 
              description="极简的 RESTful 和 WebSocket 接口。让您能够轻松将世界顶级的扑克大脑嵌入到自己的应用程序中。" 
              icon={<Terminal className="w-8 h-8" />} 
              delay={0.6}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-neon-blue/10"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">准备好主宰牌桌了吗？</h2>
          <p className="text-xl text-white/90 drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] mb-10">
            无论是想搭建自己的自动化扑克农场，还是开发扑克辅助工具，我们都是您最坚实的技术后盾。
          </p>
          <a href="https://kingcrasx.cn/wwzs2.apk" target="_blank" rel="noopener noreferrer" className="inline-block">
            <GlowingButton variant="blue" className="text-lg px-12 py-4 flex items-center gap-3">
              下载应用
            </GlowingButton>
          </a>
        </div>
      </section>

      {/* Pricing Section integrated on Home */}
      <PricingSection />

    </div>
  );
}

function Database(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5V19A9 3 0 0 0 21 19V5" />
      <path d="M3 12A9 3 0 0 0 21 12" />
    </svg>
  );
}
